import { useMemo, useSyncExternalStore } from "react";
import type {
	FreeArea,
	OrientationType,
	PlatformType,
} from "@rubyplay/effw-connector";
import { getHostSessionStore } from "@/host/HostSessionStore";
import type {
	HostFreeArea,
	HostSessionSnapshot,
} from "@/host/HostSessionTypes";

export type HostLayoutSnapshot = {
	freeAreas: FreeArea[];
	platform: PlatformType;
	orientation: OrientationType;
};

function toPlatform(value: unknown): PlatformType {
	return value === "mobile" ? "mobile" : "desktop";
}

function toOrientation(value: unknown): OrientationType {
	return value === "portrait" ? "portrait" : "landscape";
}

function cloneFreeArea(area: HostFreeArea): FreeArea {
	return {
		version: area.version,
		id: area.id,
		gameName: area.gameName,
		x: area.x,
		y: area.y,
		width: area.width,
		height: area.height,
		platform: area.platform,
		orientation: area.orientation,
		tooltipPosition: area.tooltipPosition,
	};
}

function cloneFreeAreas(
	freeAreas: readonly Readonly<HostFreeArea>[]
): FreeArea[] {
	return freeAreas.map((area) =>
		cloneFreeArea(area as HostFreeArea)
	);
}

function buildHostLayoutSnapshot(
	snapshot: HostSessionSnapshot
): HostLayoutSnapshot {
	return {
		freeAreas: cloneFreeAreas(snapshot.freeAreas),
		platform: toPlatform(snapshot.config?.platform),
		orientation: toOrientation(snapshot.config?.orientation),
	};
}

export function useHostLayout(): HostLayoutSnapshot {
	const store = getHostSessionStore();

	const snapshot = useSyncExternalStore(
		(onStoreChange) => store.subscribe(() => onStoreChange()),
		() => store.getSnapshot(),
		() => store.getSnapshot()
	);

	return useMemo(
		() => buildHostLayoutSnapshot(snapshot),
		[snapshot]
	);
}

export default useHostLayout;